import clsx from 'clsx'

export interface AvatarProps {
  src?: string | null
  name: string
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  className?: string
  status?: 'online' | 'offline' | 'away' | 'busy'
}

export default function Avatar({ src, name, size = 'md', className, status }: AvatarProps) {
  const sizes = {
    xs: 'h-6 w-6 text-[10px]',
    sm: 'h-8 w-8 text-xs',
    md: 'h-10 w-10 text-sm',
    lg: 'h-12 w-12 text-base',
    xl: 'h-16 w-16 text-lg',
  }

  const statusSizes = {
    xs: 'w-1.5 h-1.5 right-0 bottom-0',
    sm: 'w-2 h-2 right-0 bottom-0',
    md: 'w-2.5 h-2.5 right-0 bottom-0',
    lg: 'w-3 h-3 right-0 bottom-0',
    xl: 'w-3.5 h-3.5 right-0.5 bottom-0.5',
  }

  const statusColors = {
    online: 'bg-success',
    offline: 'bg-dark-500',
    away: 'bg-warning',
    busy: 'bg-error',
  }

  const getInitials = (name: string) => {
    const parts = name.split(' ')
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }

  const getColorFromName = (name: string) => {
    // Dark theme compatible colors
    const colors = [
      'bg-red-500/80',
      'bg-orange-500/80',
      'bg-amber-500/80',
      'bg-yellow-500/80',
      'bg-lime-500/80',
      'bg-green-500/80',
      'bg-emerald-500/80',
      'bg-teal-500/80',
      'bg-cyan-500/80',
      'bg-sky-500/80',
      'bg-blue-500/80',
      'bg-indigo-500/80',
      'bg-violet-500/80',
      'bg-purple-500/80',
      'bg-fuchsia-500/80',
      'bg-pink-500/80',
    ]
    const index = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length
    return colors[index]
  }

  return (
    <div className={clsx('relative inline-flex', className)}>
      {src ? (
        <img
          src={src}
          alt={name}
          className={clsx(
            'rounded-full object-cover ring-2 ring-dark-700',
            sizes[size]
          )}
        />
      ) : (
        <div
          className={clsx(
            'rounded-full flex items-center justify-center text-white font-medium',
            'ring-2 ring-dark-700',
            sizes[size],
            getColorFromName(name)
          )}
        >
          {getInitials(name)}
        </div>
      )}
      {status && (
        <span
          className={clsx(
            'absolute rounded-full ring-2 ring-surface',
            statusSizes[size],
            statusColors[status]
          )}
        />
      )}
    </div>
  )
}

export interface AvatarGroupProps {
  avatars: { src?: string | null; name: string }[]
  max?: number
  size?: AvatarProps['size']
  className?: string
}

export function AvatarGroup({ avatars, max = 4, size = 'sm', className }: AvatarGroupProps) {
  const displayAvatars = avatars.slice(0, max)
  const remaining = avatars.length - max

  const overlapSizes = {
    xs: '-ml-2',
    sm: '-ml-2',
    md: '-ml-3',
    lg: '-ml-4',
    xl: '-ml-5',
  }

  return (
    <div className={clsx('flex items-center', className)}>
      {displayAvatars.map((avatar, index) => (
        <div
          key={index}
          className={clsx('relative', index > 0 && overlapSizes[size])}
          style={{ zIndex: displayAvatars.length - index }}
        >
          <Avatar {...avatar} size={size} />
        </div>
      ))}
      {remaining > 0 && (
        <div
          className={clsx(
            'flex items-center justify-center rounded-full',
            'bg-dark-700 text-cream-muted font-medium ring-2 ring-surface',
            overlapSizes[size],
            {
              'h-6 w-6 text-[10px]': size === 'xs',
              'h-8 w-8 text-xs': size === 'sm',
              'h-10 w-10 text-sm': size === 'md',
              'h-12 w-12 text-base': size === 'lg',
              'h-16 w-16 text-lg': size === 'xl',
            }
          )}
        >
          +{remaining}
        </div>
      )}
    </div>
  )
}
